import React, { useCallback, useState } from 'react';
import { CSSObject } from '@emotion/react';
import Blocks, { Block } from '@components/blocks/Blocks';
import { injectTestId } from '@cvent/nucleus-test-automation';
import isFeatureEnabled from '@tools/isFeatureEnabled';
import { CVENT_BRANDING } from '@components/engagement/constants';
import useBreakpoints from '@hooks/useBreakpoints';
import { useTranslate } from 'nucleus-text';
import { MoodThemeProvider } from '@components/blocks/BlockThemeProvider';
import useBlockTheme from '@cvent/blocks/hooks/useTheme';
import LandingInfoContentSection from '@components/landing/LandingInfoContentSection';
import Spacer from '@components/layout/Spacer';
import { Venue, Get_Event_Features_And_SpeakersQuery as GetEventFeaturesAndSpeakersQuery } from '@graphql/gql/graphql';
import Tag from '@cvent/carina/components/Tag';
import Typography from '@cvent/blocks/components/Typography';
import getFeatureMetadata from '@tools/getFeatureMetadata';
import dynamic from 'next/dynamic';
import CventBrandingBlock from './featuredSection/CventBrandingBlock';
import DateRangeSubheader from './DateRangeSubheader';
import { hasSpeakers, hasDescriptionText, REGISTRATION_STATUS } from './landingUtils';
import EventDescription from './EventDescription';
import LandingPageHeader from './LandingPageHeader';
import RegistrationConfirmationModal from './RegistrationConfirmationModal';
import RegistrationFormSkeletonLoader from './RegistrationFormSkeletonLoader';
import SpeakersCard from './SpeakerCardV2';
import SpeakerModal from './SpeakerModal';
import RegistrationNotYetOpen from './RegistrationNotYetOpen';
import { VenueContent } from './VenueContent';

export type RegInfo = {
  firstName: string;
  lastName: string;
  email: string;
  attendeeId: string;
};

interface LandingPageContentProps {
  eventInfo: GetEventFeaturesAndSpeakersQuery['eventInfo'];
  landingPageCallToAction: React.ReactNode;
  embeddedRegistrationUrl: string;
  signupLink: string;
  environment?: string;
  loginUrl?: string;
  reflexEmbeddedRegUrl?: string;
  acceptAllCookies: boolean;
}

type SpeakerFromQuery = GetEventFeaturesAndSpeakersQuery['eventInfo']['speakers'][0];

const RegistrationForm = dynamic(() => import('./RegistrationForm'), { ssr: false });

const hasVenues = (venues: Venue[]): boolean => Array.isArray(venues) && venues?.length > 0;

const DEFAULT_SKELETON_HEIGHT = 572;

const useStyles = (regFormLoaded, hasDescription = false, hasSpeaker = false): Record<string, CSSObject> => {
  const breakpoints = useBreakpoints();
  const { mediaIsM, mediaIsL, mediaIsLargeAndAbove, mediaIsMobile } = breakpoints;
  const { block, size, font } = useBlockTheme();

  let gridTemplateArea = `"title regform""venue regform"`;
  let gridTemplateRows: string;
  if (hasSpeaker && hasDescription) {
    gridTemplateArea = `
            "title regform"
            "description regform"
            "speaker regform"
            "venue regform"
          `;
    gridTemplateRows = 'auto auto';
  } else if (hasSpeaker && !hasDescription) {
    gridTemplateArea = `
            "title regform"
            "speaker regform"
            "venue regform"
          `;
    gridTemplateRows = 'auto';
  } else if (hasDescription && !hasSpeaker) {
    gridTemplateArea = `
            "title regform"
            "description regform"
            "venue regform"
          `;
    gridTemplateRows = 'auto';
  }

  const gridResponsive = {
    gridColumn: '1 / -1',
    [mediaIsL]: {
      minWidth: 416
    },
    [mediaIsLargeAndAbove]: {
      gridColumn: '2 / 7'
    }
  };

  return {
    container: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      justifyContent: 'center',
      gridColumnGap: 16,
      gridTemplateAreas: `
              "title"
              "description"
              "regform"
              "speaker"
              "venue"
          `,
      [mediaIsM]: {
        display: 'flex',
        flexDirection: 'column',
        width: 514,
        maxWidth: 514
      },
      [mediaIsLargeAndAbove]: {
        gridColumnGap: 24,
        gridAutoRows: 'auto 1fr',
        gridTemplateColumns: 'repeat(12, 1fr)',
        gridTemplateRows,
        gridTemplateAreas: gridTemplateArea
      }
    },
    pageContentStyle: {
      minHeight: 'auto',
      [mediaIsLargeAndAbove]: {
        minHeight: '100vh'
      }
    },
    titleContainer: {
      width: '100%',
      gridArea: 'title',
      marginBottom: 32,
      [mediaIsMobile]: {
        marginBottom: 24
      },
      ...gridResponsive
    },
    description: {
      gridArea: 'description',
      marginBottom: 32,
      ...gridResponsive
    },
    speakerLabel: {
      marginTop: 0,
      marginBottom: 16,
      fontSize: font.base.size.h2,
      fontWeight: 600
    },
    speakerContent: {
      width: '100%',
      flexDirection: 'column',
      gridArea: 'speaker',
      ...gridResponsive
    },
    venueContent: {
      width: '100%',
      flexDirection: 'column',
      gridArea: 'venue',
      ...gridResponsive
    },
    speakerCard: {
      [mediaIsMobile]: {
        maxWidth: '400'
      }
    },
    skeletonLoaderContainer: {
      width: '85%',
      height: DEFAULT_SKELETON_HEIGHT,
      transition: 'height 0.25s ease-in',
      transitionDelay: '.1s',
      borderRadius: size.border.radius.fiveX,
      background: block.container,
      top: 0,
      borderWidth: size.border.base,
      borderColor: block.container,
      paddingLeft: '48px',
      paddingRight: '48px',
      position: 'absolute',
      justifyContent: 'center',
      zIndex: 1
    },
    regFormContainerColumn: {
      position: 'relative',
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      gridArea: 'regform',
      gridColumn: '1 / -1',
      marginBottom: 32,
      [mediaIsLargeAndAbove]: {
        marginBottom: hasSpeaker ? 16 : 32,
        minWidth: 375,
        gridColumn: '8 / 12'
      }
    },
    stickyRegFormContainer: {
      minHeight: regFormLoaded ? 'auto' : DEFAULT_SKELETON_HEIGHT,
      height: 'fit-content',
      position: 'sticky',
      width: '100%',
      top: 72
    }
  };
};

function LandingPageContent({
  eventInfo,
  landingPageCallToAction,
  signupLink,
  embeddedRegistrationUrl,
  environment,
  loginUrl,
  reflexEmbeddedRegUrl,
  acceptAllCookies
}: LandingPageContentProps): JSX.Element {
  const { features, end, title, start, speakers, isCancelled, status, venues } = eventInfo;
  const hasDescription = hasDescriptionText(eventInfo);
  const hasSpeaker = hasSpeakers(speakers);
  const [regFormLoaded, setRegFormLoaded] = useState(false);

  const {
    pageContentStyle,
    titleContainer,
    speakerLabel,
    speakerContent,
    speakerCard,
    skeletonLoaderContainer,
    regFormContainerColumn,
    stickyRegFormContainer,
    container,
    description,
    venueContent
  } = useStyles(regFormLoaded, hasDescription, hasSpeaker);
  const breakpoints = useBreakpoints();
  const { isMobile } = breakpoints;
  const { translate } = useTranslate();

  const isBrandingEnabled = isFeatureEnabled(features, CVENT_BRANDING);
  const publicLandingPageFeatureMetadata = getFeatureMetadata(features, 'HAS_PUBLIC_LANDING_PAGE');

  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [regInfo, setRegInfo] = useState<RegInfo>({ firstName: '', lastName: '', email: '', attendeeId: '' });
  const [iframeKey, setIframeKey] = useState(0);

  const [selectedSpeaker, setSelectedSpeaker] = useState<SpeakerFromQuery>(null);
  const [isSpeakerModalOpen, setIsSpeakerModalOpen] = useState(false);

  const closeConfirmationModal = useCallback(() => {
    setConfirmationModalOpen(false);
    setIframeKey(iframeKey + 1);
  }, [setConfirmationModalOpen, iframeKey]);

  return (
    <div {...injectTestId('landing-page-container')} css={pageContentStyle}>
      <Blocks>
        <Block>
          <LandingInfoContentSection>
            <div css={container}>
              <div css={titleContainer}>
                {isCancelled ? (
                  <div {...injectTestId('event-canceled-tag')}>
                    <Tag text={translate('Generic-Canceled-Label')} size="large" />
                  </div>
                ) : null}
                <LandingPageHeader
                  subheader={
                    Boolean(start) && Boolean(end) ? <DateRangeSubheader startDate={start} endDate={end} /> : null
                  }
                  isCancelled={isCancelled}
                  title={title}
                  eventInfo={eventInfo}
                  callToAction={publicLandingPageFeatureMetadata?.hideLoginButton ? null : landingPageCallToAction}
                />
              </div>
              {hasDescriptionText(eventInfo) && (
                <div css={description}>
                  <EventDescription eventInfo={eventInfo} numberOfLines={isMobile ? 10 : 15} />
                </div>
              )}
              <div css={regFormContainerColumn}>
                {status === REGISTRATION_STATUS.PENDING ? (
                  <RegistrationNotYetOpen />
                ) : (
                  <div css={stickyRegFormContainer}>
                    {!regFormLoaded && (
                      <div css={skeletonLoaderContainer}>
                        <RegistrationFormSkeletonLoader />
                      </div>
                    )}
                    <RegistrationForm
                      eventInfo={eventInfo}
                      embeddedRegistrationUrl={embeddedRegistrationUrl}
                      environment={environment}
                      regInfo={regInfo}
                      iframeKey={iframeKey}
                      setRegInfo={setRegInfo}
                      setConfirmationModalOpen={setConfirmationModalOpen}
                      setRegFormLoaded={setRegFormLoaded}
                      regFormLoaded={regFormLoaded}
                      reflexEmbeddedRegUrl={reflexEmbeddedRegUrl}
                      acceptAllCookies={acceptAllCookies}
                    />
                  </div>
                )}
              </div>
              {hasSpeakers(speakers) && (
                <div css={speakerContent} {...injectTestId('landing-page-speakers-container')}>
                  <Typography css={speakerLabel} as="h2">
                    {translate('LandingPage-Speakers-Label', {
                      speakers: speakers.length
                    })}
                  </Typography>
                  {speakers.map(speaker => (
                    <div key={speaker.id} css={speakerCard}>
                      <SpeakersCard
                        speaker={speaker}
                        onClick={() => {
                          if (speaker.biography) {
                            setSelectedSpeaker(speaker);
                            setIsSpeakerModalOpen(true);
                          }
                        }}
                      />
                      <Spacer size={1 / 2} />
                    </div>
                  ))}

                  {isSpeakerModalOpen && (
                    <SpeakerModal
                      speaker={selectedSpeaker}
                      isModalOpen={isSpeakerModalOpen}
                      setIsModalOpen={setIsSpeakerModalOpen}
                    />
                  )}
                </div>
              )}
              {publicLandingPageFeatureMetadata?.showVenue && hasVenues(venues) && (
                <div css={venueContent}>
                  <VenueContent venueInfo={venues[0]} />{' '}
                </div>
              )}
            </div>
          </LandingInfoContentSection>
        </Block>
        {isBrandingEnabled && (
          <Block>
            <CventBrandingBlock signupLink={signupLink} />
          </Block>
        )}
      </Blocks>
      <MoodThemeProvider>
        <RegistrationConfirmationModal
          isOpen={confirmationModalOpen}
          onDismiss={closeConfirmationModal}
          firstName={regInfo.firstName}
          lastName={regInfo.lastName}
          loginUrl={loginUrl}
          hideLoginSection={!!publicLandingPageFeatureMetadata?.hideLoginButton}
        />
      </MoodThemeProvider>
    </div>
  );
}

export default LandingPageContent;
